import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import NotificationBell from "../../components/NotificationBell";
import LanguageToggle from "../../components/LanguageToggle";
import Logo from "../../components/Logo";
import { useT } from "../../lib/i18n";
import {
  LayoutDashboard, Link2, Send, FileText, Users, Megaphone, GraduationCap,
  Settings, LogOut, Menu,
} from "lucide-react";
import { toast } from "sonner";

const NAV_KEYS = [
  { to: "/broker", labelKey: "nav.overview", Icon: LayoutDashboard, end: true, testId: "nav-overview" },
  { to: "/broker/applications", labelKey: "nav.applications", Icon: FileText, testId: "nav-applications" },
  { to: "/broker/submit", labelKey: "nav.submit", Icon: Send, testId: "nav-submit" },
  { to: "/broker/referral", labelKey: "nav.referral", Icon: Link2, testId: "nav-referral" },
  { to: "/broker/leads", labelKey: "nav.leads", Icon: Users, testId: "nav-leads" },
  { to: "/broker/marketing", labelKey: "nav.marketing", Icon: Megaphone, testId: "nav-marketing" },
  { to: "/broker/training", labelKey: "nav.training", Icon: GraduationCap, testId: "nav-training" },
  { to: "/broker/account", labelKey: "nav.account", Icon: Settings, testId: "nav-account" },
];

export default function SponsorLayout() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const NAV = NAV_KEYS.map((n) => ({ ...n, label: t(n.labelKey) }));
  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/login");
  };

  const NavLinks = ({ onClick, idSuffix = "" }) => (
    <>
      {NAV.map(({ to, label, Icon, end, testId }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onClick}
          data-testid={`${testId}${idSuffix}`}
          className={({ isActive }) =>
            `group mb-1 flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "border-l-2 border-blue-600 bg-blue-50 text-blue-700"
                : "border-l-2 border-transparent text-slate-600 hover:bg-blue-50 hover:text-blue-600"
            }`
          }
        >
          <Icon className="h-4 w-4" strokeWidth={2} />
          {label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="portal-theme flex min-h-screen text-slate-900">
      <aside className="hidden w-72 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
        <div className="flex h-16 items-center gap-2 border-b border-slate-200 px-5">
          <Logo height={40} />
        </div>
        <nav className="flex-1 overflow-y-auto p-3"><NavLinks /></nav>
        <div className="border-t border-slate-200 p-3">
          <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-rose-50 hover:text-rose-600" data-testid="broker-logout-btn">
            <LogOut className="h-4 w-4" strokeWidth={2} /> Log Out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 backdrop-blur-sm sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden text-slate-700 hover:bg-slate-100" data-testid="broker-mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 portal-theme bg-white" data-testid="broker-mobile-drawer">
                <div className="flex h-16 items-center gap-2 border-b border-slate-200 px-5">
                  <Logo height={36} />
                </div>
                <nav className="p-3"><NavLinks onClick={() => setMobileOpen(false)} idSuffix="-mobile" /></nav>
                <div className="border-t border-slate-200 p-3">
                  <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-sm text-slate-600 hover:bg-rose-50 hover:text-rose-600" data-testid="broker-logout-mobile-drawer">
                    <LogOut className="h-4 w-4" /> Log Out
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            <Logo height={32} className="md:hidden" />
            <div className="hidden text-sm sm:block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("nav.signedInAs")}</span>{" "}
              <span className="ml-2 font-semibold text-slate-900" data-testid="current-broker-name">{user?.name}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageToggle />
            <NotificationBell role="sponsor" onNavigate={(target) => nav(target)} />
          </div>
        </header>
        <main className="animate-fade-in-up flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
